import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Mail, MapPin, Phone } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const inputClass =
  "w-full rounded-lg border border-white/10 bg-canvas px-4 py-3.5 text-sm text-ink placeholder:text-dim outline-none transition-colors duration-300 focus:border-accent";

export const ContactSection = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", company: "", budget: "", services: [], message: "" });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id) ? f.services.filter((s) => s !== id) : [...f.services, id],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);
    try {
      await api.post("/contact", form);
      setStatus("success");
      setForm({ name: "", email: "", company: "", budget: "", services: [], message: "" });
    } catch {
      setStatus("error");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="border-t border-white/10" data-testid="contact-section">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-4 py-24 sm:px-8 sm:py-32 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent sm:text-sm">{t.contact.eyebrow}</p>
            <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-ink sm:text-5xl">
              {t.contact.title}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-mist sm:text-lg">{t.contact.sub}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 space-y-5">
              <a href={`mailto:${t.contact.emailUs}`} className="flex items-center gap-4 text-mist transition-colors hover:text-accent" data-testid="contact-email-link">
                <Mail className="h-5 w-5 text-accent" />
                <span className="font-mono text-sm tracking-widest">{t.contact.emailUs}</span>
              </a>
              <div className="flex items-center gap-4 text-mist">
                <Phone className="h-5 w-5 text-accent" />
                <span className="font-mono text-sm tracking-widest">{t.contact.phone}</span>
              </div>
              <div className="flex items-center gap-4 text-mist">
                <MapPin className="h-5 w-5 text-accent" />
                <span className="font-mono text-sm tracking-widest">{t.contact.address}</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {status === "success" ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex h-full min-h-[400px] flex-col items-center justify-center rounded-2xl border border-accent/40 bg-accent/5 p-12 text-center"
                data-testid="contact-success-message"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent">
                  <Check className="h-8 w-8 text-canvas" />
                </span>
                <p className="mt-8 max-w-sm font-display text-xl font-bold uppercase tracking-tight text-ink sm:text-2xl">
                  {t.contact.success}
                </p>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} className="rounded-2xl border border-white/10 bg-card p-8 sm:p-10" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-dim">{t.contact.name} *</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t.contact.namePlaceholder}
                      className={inputClass}
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-dim">{t.contact.email} *</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder={t.contact.emailPlaceholder}
                      className={inputClass}
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-dim">{t.contact.company}</label>
                    <input
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      placeholder={t.contact.companyPlaceholder}
                      className={inputClass}
                      data-testid="contact-input-company"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-dim">{t.contact.budget}</label>
                    <select
                      value={form.budget}
                      onChange={(e) => setForm({ ...form, budget: e.target.value })}
                      className={`${inputClass} appearance-none`}
                      data-testid="contact-select-budget"
                    >
                      <option value="">—</option>
                      {t.contact.budgets.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-dim">{t.contact.servicesLabel}</label>
                  <div className="flex flex-wrap gap-3">
                    {t.contact.services.map((service) => (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => toggleService(service.id)}
                        className={`rounded-full border px-4 py-2 font-mono text-xs uppercase tracking-widest transition-all duration-300 ${
                          form.services.includes(service.id)
                            ? "border-accent bg-accent text-canvas"
                            : "border-white/15 text-mist hover:border-accent/60 hover:text-ink"
                        }`}
                        data-testid={`contact-service-pill-${service.id}`}
                      >
                        {service.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-dim">{t.contact.message} *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t.contact.messagePlaceholder}
                    className={`${inputClass} resize-none`}
                    data-testid="contact-input-message"
                  />
                </div>

                {status === "error" && (
                  <p className="mt-4 font-mono text-xs uppercase tracking-widest text-red-400" data-testid="contact-error-message">
                    {t.contact.error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-accent px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 sm:w-auto"
                  data-testid="contact-submit-button"
                >
                  {sending ? t.contact.sending : t.contact.submit}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
